import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { firestore } from "../firebase";
import TopBar from "./TopBar";
import ShortlistedLeads from "./ShortlistedLeads";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { generateQuotePdfClient } from "../utils/pdf";
import { openOrDownloadPdf } from "../utils/download";
import { formatINR } from "../utils/format";
import { FiArrowLeft, FiFileText, FiUsers, FiPhone, FiCalendar, FiDownload } from "react-icons/fi";
import "../styles/quotation-system.css";

export default function UserDetail() {
  const { userId } = useParams();
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('leads'); // 'quotations' or 'leads'
  const [quotations, setQuotations] = useState([]);
  const [contactedLeadsCount, setContactedLeadsCount] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const handleQuickDateRange = (range) => {
    const today = new Date();
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    
    let start = new Date(today);
    
    switch (range) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case '3days':
        start.setDate(start.getDate() - 3);
        start.setHours(0, 0, 0, 0);
        break;
      case '7days':
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case '15days':
        start.setDate(start.getDate() - 15);
        start.setHours(0, 0, 0, 0);
        break;
      case '30days':
        start.setDate(start.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        break;
      default:
        return;
    }
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const { modal, showAlert, showConfirm, hideModal } = useModal();

  const openQuotePdf = async (quote) => {
    setGeneratingPdf(quote.id);
    try {
      const { blob, filename } = await generateQuotePdfClient({ quote });
      openOrDownloadPdf(blob, filename);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      showAlert("Failed to generate PDF. Please try again.", "error", "Error");
    } finally {
      setGeneratingPdf(null);
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    const allIds = quotations.map((q) => q.id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    showConfirm(
      `Delete ${selectedIds.length} quotation(s)? This cannot be undone.`,
      async () => {
        const batch = firestore.batch();
        selectedIds.forEach((id) => {
          const ref = firestore.collection("quotes").doc(id);
          batch.delete(ref);
        });
        await batch.commit();
        setSelectedIds([]);
        showAlert(`${selectedIds.length} quotation(s) deleted successfully.`, "success", "Deleted");
      },
      "Delete Quotations",
      "warning"
    );
  };

  useEffect(() => {
    document.body.classList.add('quotation-page');
    return () => {
      document.body.classList.remove('quotation-page');
    };
  }, []);

  useEffect(() => {
    if (!userId) return;

    // Load user data
    firestore.collection("users").doc(userId).get().then(doc => {
      if (doc.exists) {
        setUser({ id: doc.id, ...doc.data() });
      }
      setLoading(false);
    });

    // Load quotations
    const unsubscribeQuotes = firestore
      .collection("quotes")
      .where("meta.salespersonUid", "==", userId)
      .orderBy("meta.createdAt", "desc")
      .onSnapshot((snapshot) => {
        const quotes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setQuotations(quotes);
      });

    return () => {
      unsubscribeQuotes();
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // Load contacted leads count for date range
    const startDate = new Date(dateRange.start);
    startDate.setHours(0, 0, 0, 0);
    const startTimestamp = startDate.getTime();
    
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999);
    const endTimestamp = endDate.getTime();

    // Try the indexed query first, but always have a fallback
    const loadContactedLeads = async () => {
      try {
        // First try with Firestore query (if index exists)
        const querySnapshot = await firestore
          .collection("contactedLeads")
          .where("userId", "==", userId)
          .get();

        // Filter client-side to handle date range
        const count = querySnapshot.docs.filter(doc => {
          const data = doc.data();
          // Handle different timestamp formats
          let timestamp = null;
          
          if (data.contactedTimestamp) {
            // If it's a number (milliseconds)
            timestamp = typeof data.contactedTimestamp === 'number' 
              ? data.contactedTimestamp 
              : data.contactedTimestamp.toMillis ? data.contactedTimestamp.toMillis() : null;
          } else if (data.contactedAt) {
            // Fallback to contactedAt
            timestamp = typeof data.contactedAt === 'number' 
              ? data.contactedAt 
              : data.contactedAt.toMillis ? data.contactedAt.toMillis() : null;
          } else if (data.timestamp) {
            // Another fallback
            timestamp = typeof data.timestamp === 'number' 
              ? data.timestamp 
              : data.timestamp.toMillis ? data.timestamp.toMillis() : null;
          }
          
          if (timestamp === null) return false;
          
          return timestamp >= startTimestamp && timestamp <= endTimestamp;
        }).length;
        
        setContactedLeadsCount(count);
      } catch (error) {
        console.error("Error loading contacted leads:", error);
        // If all fails, set to 0
        setContactedLeadsCount(0);
      }
    };

    loadContactedLeads();
  }, [userId, dateRange]);

  if (loading) {
    return (
      <div className="quotation-system">
        <TopBar />
        <div className="elegant-container fade-in-up">
          <div className="elegant-card" style={{ textAlign: "center", padding: "4rem 2rem" }}>
            <span className="loading-elegant"></span>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="quotation-system">
        <TopBar />
        <div className="elegant-container fade-in-up">
          <div className="elegant-card">
            <p>User not found</p>
            <button onClick={() => history.push("/admin/users")} className="btn-elegant btn-elegant-secondary">
              Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quotation-system">
      <TopBar />
      <Modal {...modal} onClose={hideModal} />
      <div className="elegant-container fade-in-up">
        <div className="elegant-card">
          <button
            onClick={() => history.push("/admin/users")}
            className="btn-elegant btn-elegant-secondary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "1.5rem"
            }}
          >
            <FiArrowLeft size={18} />
            Back to Users
          </button>

          <div style={{ marginBottom: "2rem" }}>
            <h1 className="elegant-title" style={{ marginBottom: "0.5rem" }}>
              {user.name || user.email}
            </h1>
            <p className="elegant-subtitle" style={{ marginBottom: "1rem" }}>
              {user.email} {user.phone && `• ${user.phone}`}
            </p>

            {/* Contacted Leads Count with Date Range */}
            <div
              style={{
                background: "rgba(139, 92, 246, 0.1)",
                border: "1px solid rgba(139, 92, 246, 0.3)",
                borderRadius: "8px",
                padding: "1rem",
                marginBottom: "1.5rem"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
                <FiPhone size={18} style={{ color: "var(--accent-purple)" }} />
                <strong style={{ color: "var(--primary-deep)" }}>Contacted Leads</strong>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <FiCalendar size={16} />
                    <label style={{ fontSize: "0.875rem", color: "var(--neutral-600)", fontWeight: "500", whiteSpace: "nowrap" }}>From:</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      style={{
                        padding: "0.625rem 0.75rem",
                        borderRadius: "8px",
                        border: "1px solid var(--neutral-300)",
                        fontSize: "0.875rem",
                        fontFamily: "inherit",
                        backgroundColor: "white",
                        transition: "all var(--transition-base)",
                        minWidth: "140px"
                      }}
                      onFocus={(e) => e.target.style.borderColor = "var(--accent-purple)"}
                      onBlur={(e) => e.target.style.borderColor = "var(--neutral-300)"}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <label style={{ fontSize: "0.875rem", color: "var(--neutral-600)", fontWeight: "500", whiteSpace: "nowrap" }}>To:</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      style={{
                        padding: "0.625rem 0.75rem",
                        borderRadius: "8px",
                        border: "1px solid var(--neutral-300)",
                        fontSize: "0.875rem",
                        fontFamily: "inherit",
                        backgroundColor: "white",
                        transition: "all var(--transition-base)",
                        minWidth: "140px"
                      }}
                      onFocus={(e) => e.target.style.borderColor = "var(--accent-purple)"}
                      onBlur={(e) => e.target.style.borderColor = "var(--neutral-300)"}
                    />
                  </div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "var(--accent-purple)", marginLeft: "auto" }}>
                    {contactedLeadsCount} leads
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    onClick={() => handleQuickDateRange('today')}
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      border: "1px solid var(--neutral-300)",
                      backgroundColor: "white",
                      color: "var(--neutral-700)",
                      cursor: "pointer",
                      transition: "all var(--transition-base)",
                      whiteSpace: "nowrap"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "var(--neutral-50)";
                      e.target.style.borderColor = "var(--accent-purple)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.borderColor = "var(--neutral-300)";
                    }}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => handleQuickDateRange('3days')}
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      border: "1px solid var(--neutral-300)",
                      backgroundColor: "white",
                      color: "var(--neutral-700)",
                      cursor: "pointer",
                      transition: "all var(--transition-base)",
                      whiteSpace: "nowrap"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "var(--neutral-50)";
                      e.target.style.borderColor = "var(--accent-purple)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.borderColor = "var(--neutral-300)";
                    }}
                  >
                    Last 3 Days
                  </button>
                  <button
                    onClick={() => handleQuickDateRange('7days')}
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      border: "1px solid var(--neutral-300)",
                      backgroundColor: "white",
                      color: "var(--neutral-700)",
                      cursor: "pointer",
                      transition: "all var(--transition-base)",
                      whiteSpace: "nowrap"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "var(--neutral-50)";
                      e.target.style.borderColor = "var(--accent-purple)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.borderColor = "var(--neutral-300)";
                    }}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => handleQuickDateRange('15days')}
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      border: "1px solid var(--neutral-300)",
                      backgroundColor: "white",
                      color: "var(--neutral-700)",
                      cursor: "pointer",
                      transition: "all var(--transition-base)",
                      whiteSpace: "nowrap"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "var(--neutral-50)";
                      e.target.style.borderColor = "var(--accent-purple)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.borderColor = "var(--neutral-300)";
                    }}
                  >
                    Last 15 Days
                  </button>
                  <button
                    onClick={() => handleQuickDateRange('30days')}
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                      borderRadius: "6px",
                      border: "1px solid var(--neutral-300)",
                      backgroundColor: "white",
                      color: "var(--neutral-700)",
                      cursor: "pointer",
                      transition: "all var(--transition-base)",
                      whiteSpace: "nowrap"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "var(--neutral-50)";
                      e.target.style.borderColor = "var(--accent-purple)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                      e.target.style.borderColor = "var(--neutral-300)";
                    }}
                  >
                    Last 30 Days
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                borderBottom: "2px solid var(--neutral-200)",
                marginBottom: "2rem"
              }}
            >
              <button
                onClick={() => setActiveTab('quotations')}
                className="btn-elegant"
                style={{
                  background: activeTab === 'quotations' ? "var(--gradient-primary)" : "transparent",
                  color: activeTab === 'quotations' ? "white" : "var(--neutral-700)",
                  border: activeTab === 'quotations' ? "none" : "2px solid var(--neutral-300)",
                  borderRadius: "8px 8px 0 0",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "-2px",
                  borderBottom: activeTab === 'quotations' ? "2px solid transparent" : "2px solid var(--neutral-200)"
                }}
              >
                <FiFileText size={18} />
                Quotations ({quotations.length})
              </button>
              <button
                onClick={() => setActiveTab('leads')}
                className="btn-elegant"
                style={{
                  background: activeTab === 'leads' ? "var(--gradient-primary)" : "transparent",
                  color: activeTab === 'leads' ? "white" : "var(--neutral-700)",
                  border: activeTab === 'leads' ? "none" : "2px solid var(--neutral-300)",
                  borderRadius: "8px 8px 0 0",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "-2px",
                  borderBottom: activeTab === 'leads' ? "2px solid transparent" : "2px solid var(--neutral-200)"
                }}
              >
                <FiUsers size={18} />
                Leads
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'quotations' ? (
            <div>
              {quotations.length === 0 ? (
                <div style={{ textAlign: "center", padding: "4rem 2rem", color: "var(--neutral-600)" }}>
                  <p>No quotations found for this user.</p>
                </div>
              ) : (
                <>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    marginBottom: "1.5rem",
                    flexWrap: "wrap",
                    gap: "1rem"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <input
                          type="checkbox"
                          onChange={toggleAll}
                          checked={
                            quotations.length > 0 &&
                            quotations.every((q) => selectedIds.includes(q.id))
                          }
                          style={{ 
                            minWidth: "24px", 
                            minHeight: "24px",
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            touchAction: "manipulation"
                          }}
                        />
                      <span style={{ color: "var(--neutral-600)", fontWeight: "600", fontSize: "0.9375rem" }}>
                        Select All
                      </span>
                    </div>
                    {selectedIds.length > 0 && (
                      <button
                        onClick={deleteSelected}
                        className="btn-elegant btn-elegant-danger"
                        style={{ 
                          padding: "0.75rem 1.5rem", 
                          minHeight: "48px",
                          minWidth: "180px"
                        }}
                      >
                        Delete {selectedIds.length} selected
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gap: "1rem",
                      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))"
                    }}
                  >
                    {quotations.map((quote) => (
                    <div
                      key={quote.id}
                      style={{
                        background: "white",
                        border: selectedIds.includes(quote.id) ? "2px solid var(--accent-purple)" : "2px solid var(--neutral-200)",
                        borderRadius: "12px",
                        padding: "1.5rem",
                        transition: "all 0.3s",
                        boxShadow: selectedIds.includes(quote.id) ? "0 4px 20px rgba(139, 92, 246, 0.15)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
                        position: "relative"
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedIds.includes(quote.id)) {
                          e.currentTarget.style.borderColor = "var(--accent-purple)";
                          e.currentTarget.style.boxShadow = "0 4px 20px rgba(139, 92, 246, 0.15)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedIds.includes(quote.id)) {
                          e.currentTarget.style.borderColor = "var(--neutral-200)";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }
                      }}
                    >
                      <div style={{ 
                        display: "flex", 
                        alignItems: "flex-start", 
                        justifyContent: "space-between",
                        marginBottom: "0.75rem"
                      }}>
                        <h4 style={{ 
                          fontSize: "1.125rem", 
                          fontWeight: "600", 
                          color: "var(--primary-deep)", 
                          lineHeight: "1.4",
                          flex: 1,
                          margin: 0
                        }}>
                          {quote.meta?.quoteNo || quote.meta?.quoteNumber || `Quote #${quote.id}`}
                        </h4>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(quote.id)}
                          onChange={() => toggleOne(quote.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            minWidth: "24px", 
                            minHeight: "24px",
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            marginLeft: "0.5rem",
                            touchAction: "manipulation"
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", marginBottom: "0.5rem" }}>
                        <strong>Customer:</strong> {quote.customer?.name || 'N/A'}
                      </div>
                      {quote.derived?.capacityKw && (
                        <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", marginBottom: "0.5rem" }}>
                          <strong>Capacity:</strong> {quote.derived.capacityKw} kW
                        </div>
                      )}
                      <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", marginBottom: "0.5rem" }}>
                        <strong>Total Amount:</strong> {(() => {
                          // Calculate total amount including GST
                          // totalPayable is the final amount (includes GST, logistics, minus subsidy if applicable)
                          let total = 0;
                          
                          if (quote.derived?.totalPayable) {
                            // totalPayable might not include subsidy deduction, so calculate final amount
                            if (quote.derived.subsidyApplicable && quote.derived.subsidyAmount) {
                              total = quote.derived.totalBeforeSubsidy - quote.derived.subsidyAmount;
                            } else {
                              total = quote.derived.totalPayable;
                            }
                          } else if (quote.derived?.totalBeforeSubsidy) {
                            // Fallback: calculate from totalBeforeSubsidy
                            total = quote.derived.totalBeforeSubsidy - (quote.derived.subsidyAmount || 0);
                          } else if (quote.totals?.grandTotal) {
                            total = quote.totals.grandTotal;
                          }
                          
                          return formatINR(total);
                        })()}
                      </div>
                      {quote.meta?.createdAt && (
                        <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", marginBottom: "1rem" }}>
                          <strong>Created:</strong> {(() => {
                            const date = new Date(quote.meta.createdAt);
                            const day = date.getDate().toString().padStart(2, '0');
                            const month = date.toLocaleDateString("en-GB", { month: "long" });
                            const year = date.getFullYear();
                            const hours = date.getHours().toString().padStart(2, '0');
                            const minutes = date.getMinutes().toString().padStart(2, '0');
                            return `${day} ${month} ${year}, ${hours}:${minutes}`;
                          })()}
                        </div>
                      )}
                      <button
                        onClick={() => openQuotePdf(quote)}
                        disabled={generatingPdf === quote.id}
                        className="btn-elegant btn-elegant-accent"
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.5rem",
                          marginTop: "0.75rem",
                          padding: "0.75rem 1rem"
                        }}
                      >
                        {generatingPdf === quote.id ? (
                          <>
                            <span className="loading-elegant" style={{ width: "16px", height: "16px", borderWidth: "2px" }}></span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <FiDownload size={18} />
                            {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? "Download PDF" : "View PDF"}
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <ShortlistedLeads viewUserId={userId} />
          )}
        </div>
      </div>
    </div>
  );
}

